import React, { useEffect, useMemo, useState } from "react";
import { ConfigProvider, FloatButton, Modal, Popover } from "antd";
import { BellContent } from "./BellContent";
import { ErrorBoundary } from "@companion-core/shared/app/Components/ErrorBoundary";
import { BellError } from "@companion-core/shared/app/Components/Bell/Error";
import { BellHeader } from "@companion-core/shared/app/Components/Bell/Header/BellHeader";
import { FontIcon } from "@companion-core/shared/app/Components/FontIcon";
import { IntroductionPopover } from "@companion-core/shared/app/Components/Bell/IntroductionPopover";
import { useAuthStore } from "@companion-core/shared/app/Store/authStore";
import { useBellStore } from "@companion-core/shared/app/Store/bellStore";
import { useConfigStore } from "@companion-core/shared/app/Store/configStore";
import { useI18nStore } from "@companion-core/shared/app/Store/i18nStore";
import { trackClickBell } from "@companion-core/shared/app/tracking/bell";
import { useMediaQuery } from "Utilities/mediaQuery";
import { lockScroll, unlockScroll } from "Utilities/scroll";
import { getAntdLocale, getLang } from "Utilities/lang";
import { setMetaThemeColor } from "Utilities/meta";
import { antdComponentsTheme } from "Utilities/antdTheme";
import { useTranslation } from "react-i18next";
import colors from "Assets/Styles/scss/_exports.module.scss";
import "Assets/Styles/scss/bell.scss";

interface BellProps {
  isPopover?: boolean;
}

export const Bell = ({ isPopover = true }: BellProps) => {
  const { t } = useTranslation();
  const { authenticated } = useAuthStore((state) => ({ authenticated: state.authenticated }));
  const { config, fetching } = useConfigStore();
  const {
    isPopUpVisible,
    setIsPopUpVisible,
    setBellContent,
    clearBell,
    setIsIntroPopUpVisible,
    isIntroPopUpVisible,
  } = useBellStore((state) => ({
    isPopUpVisible: state.isPopUpVisible,
    isIntroPopUpVisible: state.isIntroPopUpVisible,
    setIsPopUpVisible: state.setIsPopUpVisible,
    setIsIntroPopUpVisible: state.setIsIntroPopUpVisible,
    setBellContent: state.setBellContent,
    clearBell: state.clearBell,
  }));
  const isMobileSize = useMediaQuery("(max-width: 768px)");
  const bellModule = useMemo(() => config?.modules?.concierge?.modules?.bell, [config]);
  const isBellEnabled: boolean = useMemo(() => bellModule?.enabled, [bellModule]);
  const isBellHomeEnabled: boolean = useMemo(() => bellModule?.options.home.enabled, [bellModule]);
  const { antdLocale } = useI18nStore((state) => ({ antdLocale: state.antdLocale }));
  const [isFetching, setIsFetching] = useState(false);

  const toggleBell = (event: "close" | "reduce" = "close"): void => {
    const eventName = event == "close" ? "Close_Bell" : "Reduce_Bell";
    trackClickBell(isPopUpVisible ? eventName : "Open_Bell");
    setIsPopUpVisible(!isPopUpVisible);
  };

  // Lock scroll in background when bell is opened on mobile size
  useEffect(() => {
    if (isMobileSize) {
      if (isPopUpVisible) {
        lockScroll();
        setMetaThemeColor("concierge");
      } else {
        unlockScroll();
        setMetaThemeColor("default");
      }
    } else {
      setMetaThemeColor("default");
    }
  }, [isMobileSize, isPopUpVisible]);

  useEffect(() => {
    setBellContent(isBellHomeEnabled ? "home" : "categories");
  }, [isBellHomeEnabled, config]);

  useEffect(() => {
    setIsPopUpVisible(!isPopover);
  }, [isPopover]);

  useEffect(() => {
    setIsFetching(!!fetching && Object.keys(fetching).some((key) => fetching[key]));
  }, [fetching]);

  if (isFetching || !authenticated || !isBellEnabled) return <></>;

  return (
    <ConfigProvider
      locale={antdLocale ?? getAntdLocale(getLang())}
      theme={{
        token: {
          colorPrimary: colors.textPrimary,
          fontFamily: "Primary",
        },
        components: antdComponentsTheme,
      }}>
      <Modal
        open={isPopUpVisible}
        title={<BellHeader onClose={toggleBell} />}
        onCancel={() => toggleBell("reduce")}
        footer={<></>}
        className="bell-popover"
        keyboard
        centered={false}
        closable={false}
        afterOpenChange={(open) => {
          if (open) {
            setTimeout(() => document.getElementById("bell-dialog-title")?.focus(), 0);
          }
        }}>
        <ErrorBoundary
          context="bell-popin"
          fallback={(reset) => (
            <BellError
              testID="bell__content--error"
              buttonTestID="bell__content--error-retry"
              onClick={() => {
                reset();
                clearBell(isBellHomeEnabled ? "home" : "categories");
              }}
            />
          )}>
          <BellContent onClose={toggleBell} />
        </ErrorBoundary>
      </Modal>
      <Popover
        placement="topRight"
        open={isIntroPopUpVisible && !isMobileSize && !bellModule?.options?.isPopUpVisibleByDefault}
        classNames={{ root: "bell-intro-popover" }}
        content={<IntroductionPopover onClose={() => setIsIntroPopUpVisible(false)} />}>
        <FloatButton
          data-testid="bell__toggle-button"
          type="primary"
          aria-label={t(
            `common.bell.accessibility.float-button.${isPopUpVisible ? "open" : "closed"}`
          )}
          aria-haspopup="dialog"
          aria-expanded={isPopUpVisible}
          aria-controls={isPopUpVisible ? "bell-popover" : undefined}
          className={`bell__float-button ${!isPopover && "bell__float-button--no-popover"} ${isMobileSize && "d-none"}`}
          icon={
            !isPopUpVisible ? (
              <FontIcon
                name="JP-Concierge-bell"
                size={28}
                className="bell__icon bell__icon--concierge-bell"
                aria-hidden
              />
            ) : (
              <FontIcon name="JP-Minus" size={32} className="bell__icon" aria-hidden />
            )
          }
          onClick={() => {
            toggleBell("reduce");
            if (isIntroPopUpVisible) setIsIntroPopUpVisible(false);
          }}
        />
      </Popover>
    </ConfigProvider>
  );
};
